import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  FaUniversity,
  FaShieldAlt,
  FaFileAlt,
  FaHandsHelping,
  FaCheckCircle,
  FaArrowRight,
  FaDonate,
  FaBalanceScale,
  FaSearch,
  FaClipboardCheck,
  FaChartLine,
} from 'react-icons/fa';
import '../styles/ServicesMVP.css';
import DonationWidgetFeature from '../components/services/DonationWidgetFeature';

const serviceCards = [
  {
    icon: <FaUniversity />,
    title: '501(c)(3) Fiscal Sponsorship',
    description:
      'Operate under an established nonprofit structure so your charitable project can move forward without first forming and maintaining a separate nonprofit organization.',
  },
  {
    icon: <FaDonate />,
    title: 'Donation Processing & Receipting',
    description:
      'Approved charitable contributions are received through our nonprofit structure, with donation processing and donor receipting handled in support of the project.',
  },
  {
    icon: <FaChartLine />,
    title: 'Fund Visibility & Stewardship',
    description:
      'Project activity is tracked within our internal fund structure to support organized stewardship, financial visibility, and responsible management of charitable funds.',
  },
  {
    icon: <FaShieldAlt />,
    title: 'Compliance & Oversight',
    description:
      'We maintain the oversight framework needed to support charitable operations responsibly, including internal controls, review processes, and alignment with nonprofit requirements.',
  },
  {
    icon: <FaFileAlt />,
    title: 'Back-Office Administrative Support',
    description:
      'We provide the administrative foundation needed to support project operations, including internal processing, documentation, and core nonprofit infrastructure.',
  },
  {
    icon: <FaBalanceScale />,
    title: 'Responsible Financial Administration',
    description:
      'Funds are managed within the fiscal sponsor’s structure with appropriate review, stewardship, and accountability to help ensure resources are used in furtherance of the project’s charitable purpose.',
  },
];

const fitCards = [
  {
    icon: <FaHandsHelping />,
    title: 'Charitable Projects',
    description:
      'Projects with a legitimate charitable purpose that need an established 501(c)(3) structure to operate under.',
  },
  {
    icon: <FaSearch />,
    title: 'Growing Initiatives',
    description:
      'Initiatives that need operational infrastructure, administrative support, and financial stewardship to function effectively.',
  },
  {
    icon: <FaClipboardCheck />,
    title: 'Founders & Program Leaders',
    description:
      'People leading charitable work who want a compliant, practical structure to support execution, oversight, and growth.',
  },
];

const includedItems = [
  'Launch charitable work without first forming a separate 501(c)(3)',
  'Receive tax-deductible donations through an established nonprofit structure',
  'Reduce administrative burden through built-in operational support',
  'Access nonprofit oversight, financial stewardship, and internal controls',
  'Avoid many of the upfront costs and delays of starting a standalone organization',
  'Stay focused on mission execution while core administrative functions are supported',
];

const benefitItems = [
  'Avoid the time, cost, and administrative burden of launching and maintaining a new nonprofit entity',
  'Operate within an established nonprofit framework rather than building one from scratch',
  'Create a faster path to fundraising and charitable operations for an approved project',
  'Benefit from built-in structure for compliance-minded review, oversight, and stewardship',
  'Reduce back-office strain on project leaders through administrative support',
  'Focus more energy on mission and programmatic work instead of organizational overhead',
];

const ServiceMVP = () => {
  return (
    <>
      <Helmet>
        <title>Fiscal Sponsorship Services - Cause & Impact</title>
        <meta
          name="description"
          content="Learn how Cause & Impact provides fiscal sponsorship, donation processing, branded donation widgets, donor receipting, fund visibility, compliance support, and back-office administration for charitable projects."
        />
      </Helmet>

      <div className="services-mvp-page">
        <div className="services-mvp-bg" aria-hidden="true" />

        <section className="services-mvp-hero">
          <div className="services-mvp-container">
            <div className="services-mvp-hero-content">
              <span className="services-mvp-eyebrow">Cause & Impact Services</span>
              <h1>Fiscal Sponsorship Services</h1>
              <p className="services-mvp-hero-text">
                We provide the nonprofit structure, oversight, donation processing,
                financial stewardship, and administrative support charitable projects
                need to operate responsibly.
              </p>

              <div className="services-mvp-hero-actions">
                <Link to="/apply" className="services-mvp-btn services-mvp-btn-primary">
                  Apply Now
                  <FaArrowRight className="services-mvp-btn-icon" />
                </Link>

                <Link to="/contact" className="services-mvp-btn services-mvp-btn-secondary">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="services-mvp-section">
          <div className="services-mvp-container">
            <div className="services-mvp-section-heading">
              <h2>What We Provide</h2>
              <p>
                Our services are centered on the real role of a fiscal sponsor:
                providing the nonprofit structure, stewardship, and operational
                support needed to help charitable projects move forward responsibly.
              </p>
            </div>

            <div className="services-mvp-grid">
              {serviceCards.map((card, index) => (
                <article className="services-mvp-card" key={index}>
                  <div className="services-mvp-card-icon-wrap">
                    <div className="services-mvp-card-icon" aria-hidden="true">
                      {card.icon}
                    </div>
                  </div>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <DonationWidgetFeature />

        <section className="services-mvp-section services-mvp-section-alt">
          <div className="services-mvp-container">
            <div className="services-mvp-section-heading">
              <h2>How It Works</h2>
              <p>
                We review each project carefully to determine fit, charitable
                alignment, and the right operational structure for responsible
                fiscal sponsorship.
              </p>
            </div>

            <div className="services-mvp-process">
              <div className="services-mvp-process-step">
                <span className="services-mvp-process-num">1</span>
                <div>
                  <h3>Apply</h3>
                  <p>
                    Share your project, mission, intended activities, and what you are
                    seeking support for.
                  </p>
                </div>
              </div>

              <div className="services-mvp-process-step">
                <span className="services-mvp-process-num">2</span>
                <div>
                  <h3>Review</h3>
                  <p>
                    We evaluate charitable alignment, operational fit, and whether
                    fiscal sponsorship is the appropriate structure for the work.
                  </p>
                </div>
              </div>

              <div className="services-mvp-process-step">
                <span className="services-mvp-process-num">3</span>
                <div>
                  <h3>Structure</h3>
                  <p>
                    If approved, we determine the right sponsorship structure based on
                    the project’s actual needs and compliance realities.
                  </p>
                </div>
              </div>

              <div className="services-mvp-process-step">
                <span className="services-mvp-process-num">4</span>
                <div>
                  <h3>Operate Responsibly</h3>
                  <p>
                    Once onboarded, your project can begin operating within the
                    approved fiscal sponsorship framework.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="services-mvp-section">
          <div className="services-mvp-container">
            <div className="services-mvp-section-heading">
              <h2>Who This Is For</h2>
              <p>
                We work with charitable projects, emerging initiatives, and
                mission-driven leaders who need nonprofit infrastructure,
                administrative support, and responsible oversight behind their work.
              </p>
            </div>

            <div className="services-mvp-grid services-mvp-grid-fit">
              {fitCards.map((card, index) => (
                <article className="services-mvp-card services-mvp-card-fit" key={index}>
                  <div className="services-mvp-card-icon-wrap">
                    <div className="services-mvp-card-icon" aria-hidden="true">
                      {card.icon}
                    </div>
                  </div>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="services-mvp-section services-mvp-section-alt">
          <div className="services-mvp-container">
            <div className="services-mvp-section-heading">
              <h2>Why Projects Choose Fiscal Sponsorship</h2>
              <p>
                Fiscal sponsorship gives charitable projects a way to operate under an
                established nonprofit structure without taking on the full burden of
                starting and managing an independent 501(c)(3) from day one.
              </p>
            </div>

            <div className="services-mvp-included-grid">
              <div className="services-mvp-list-card">
                <h3>What Fiscal Sponsorship Provides</h3>
                <ul>
                  {includedItems.map((item, index) => (
                    <li key={index}>
                      <FaCheckCircle className="services-mvp-list-icon included" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="services-mvp-list-card">
                <h3>Compared to Starting Your Own Nonprofit</h3>
                <ul>
                  {benefitItems.map((item, index) => (
                    <li key={index}>
                      <FaCheckCircle className="services-mvp-list-icon included" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="services-mvp-cta">
          <div className="services-mvp-container">
            <div className="services-mvp-cta-card">
              <h2>See if Your Project Is a Fit</h2>
              <p>
                If you have a charitable initiative and want to explore whether fiscal
                sponsorship is the right path, start with the application and we’ll
                review the appropriate next step.
              </p>

              <div className="services-mvp-hero-actions">
                <Link to="/apply" className="services-mvp-btn services-mvp-btn-primary">
                  Apply Now
                  <FaArrowRight className="services-mvp-btn-icon" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServiceMVP;